import { getValue, setValue } from './transform';

/**
 * Taking a document apart and putting it back together, the way a tenant asked for.
 *
 * Some tenants do not want the document they were sent. They want the carrier bag taken off it.
 * They want the menswear lines to become a separate document addressed to a different unit, with
 * its totals recounted so each one stands on its own. That is a decision about their business,
 * not about our code, so all of it is written down here and nothing about bills, items, stores or
 * money is assumed: every field this engine touches is one an author named.
 *
 * The result is a list, because one document in can be several out. Each leg says which unit it
 * belongs to, so the route can send each one where it goes.
 */

/**
 * One item matches a combination when every entry in it agrees. A list of combinations matches
 * when any one of them does — the same "all of these, or all of those" the rule server uses.
 */
export type Combination = Record<string, string | string[]>;

export interface ExcludeRule {
  id: string;
  label?: string;
  match: Combination[];
}

/**
 * Items that move somewhere else, and what the document they move into says about itself.
 *
 * `set` is plain field-to-value: whoever writes the rule says which fields name the unit, the
 * store or anything else, because the vendor's document decides what those fields are called.
 */
export interface Reassignment {
  id: string;
  label?: string;
  /** Narrows the rule to documents where a field holds one of these values. Empty means all. */
  when?: { field: string; equals: string[] };
  match: Combination[];
  /** True moves what matched. False moves everything that did not, which is the "keep only" shape. */
  moveMatching: boolean;
  set: Record<string, string>;
}

/** A document total counted back up from the items that survived. */
export interface SumRule {
  target: string;
  /** Item fields, in order; the first one an item actually has is the one counted. */
  from: string[];
}

/**
 * Money already split across payment methods, when the amount it was split over has changed.
 *
 * One method can be held back rather than scaled — a loyalty balance is spent, not apportioned —
 * and which method that is, and where its size comes from, are both authored.
 */
export interface SplitMoney {
  path: string;
  valueField: string;
  modeField: string;
  heldMode?: string;
  heldFrom?: string;
}

export interface Recalculate {
  sums: SumRule[];
  money?: SplitMoney;
}

/**
 * A document that reverses a whole bill rather than part of one.
 *
 * A vendor sends the lines that came back when part of a bill is returned, and nothing at all
 * when the whole of it is — the same shape a cancellation arrives in. Nothing about such a
 * document can be matched, moved or counted: it has no items to work from.
 *
 * What it reverses is the original bill, and the original may have been split across units. So
 * the original is looked up, and one document is made for each unit it reached, carrying that
 * unit's own amounts with their signs turned around.
 *
 * Every field here is the author's: which field says this is a reversal, which names the original,
 * what to copy from each leg of it, and what to negate.
 */
export interface Reversal {
  /** What marks a document as reversing something — a kind field and the values that mean it. */
  when: { field: string; equals: string[] };
  /** The field naming the bill being reversed. */
  originalField: string;
  /** Copied from the original leg as it stands: `documentField: legField`. */
  set?: Record<string, string>;
  /** Taken from the original leg with its sign turned around: `documentField: legField`. */
  negate?: Record<string, string>;
}

/** One leg of the original bill, as the system that recorded it answered. */
export type OriginalLeg = Record<string, unknown>;

export interface Reshape {
  /** Where the items live on the document. */
  itemsPath: string;
  /** Other places on an item worth looking for a field, such as a nested detail object. */
  lookIn?: string[];
  /** Which field on the document names the unit it belongs to. */
  unitField?: string;
  exclude?: ExcludeRule[];
  reassign?: Reassignment[];
  recalculate?: Recalculate;
  /** What to do with a document that reverses a whole bill and so carries no items. */
  reversal?: Reversal;
  /** Documents sent nowhere: a field holding one of these values, such as an online-store channel. */
  skip?: { field: string; equals: string[] }[];
}

/**
 * Casa's own name for a field, and the name this vendor's payload actually uses for it.
 *
 * Casa keeps these per tenant and business unit: a custom field is `CF1` everywhere inside Casa,
 * and the integration that fills it calls it `division`. A rule is written against Casa's name —
 * that is the name a person recognises, and it does not change when a vendor renames theirs — and
 * translated to the vendor's name at the moment it runs, exactly as the rule server does.
 *
 * A name nobody has mapped is left alone, so a rule can always name a raw field directly.
 */
export type FieldMap = Record<string, string>;

const rename = (name: string, map: FieldMap): string => map[name] ?? name;

const translateCombinations = (combinations: Combination[] = [], map: FieldMap): Combination[] =>
  combinations.map((combination) => {
    const translated: Combination = {};
    for (const [field, value] of Object.entries(combination)) translated[rename(field, map)] = value;

    return translated;
  });

/**
 * The same rules, said in the vendor's words.
 *
 * Only the names of fields on an item are translated. What a document calls its unit, its store or
 * its totals is the vendor's own vocabulary and was authored as such; only the item fields go
 * through Casa's mapping, which is the only place the two vocabularies meet.
 */
export const translateReshape = (config: Reshape, map: FieldMap | undefined): Reshape => {
  if (!map || Object.keys(map).length === 0) return config;

  return {
    ...config,
    exclude: config.exclude?.map((rule) => ({ ...rule, match: translateCombinations(rule.match, map) })),
    reassign: config.reassign?.map((rule) => ({ ...rule, match: translateCombinations(rule.match, map) })),
    recalculate: config.recalculate && {
      ...config.recalculate,
      sums: config.recalculate.sums.map((sum) => ({ ...sum, from: sum.from.map((field) => rename(field, map)) })),
      money: config.recalculate.money && {
        ...config.recalculate.money,
        heldFrom: config.recalculate.money.heldFrom
          ? rename(config.recalculate.money.heldFrom, map)
          : config.recalculate.money.heldFrom,
      },
    },
  };
};

/** One document out, and where it goes. */
export interface Leg {
  document: Record<string, unknown>;
  unitId?: string;
  /** What happened to it, in order, so an author can see why they got what they got. */
  why: string[];
}

export const RESHAPE_RESOURCE = 'reshape.json';

const asArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? (value.filter((item) => item && typeof item === 'object') as Record<string, unknown>[]) : [];

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * A field on an item, looked for on the item itself and then wherever else the author said.
 *
 * Vendors nest. The same `division` can sit on the line or inside the line's product detail, and
 * a rule written once should find it either way.
 */
export const itemField = (item: Record<string, unknown>, key: string, lookIn: string[] = []): unknown => {
  const direct = getValue(item, key);
  if (direct !== undefined && direct !== null) return direct;

  for (const place of lookIn) {
    const nested = getValue(item, `${place}.${key}`);
    if (nested !== undefined && nested !== null) return nested;
  }
  return undefined;
};

const agrees = (actual: unknown, wanted: string | string[]): boolean => {
  if (actual === undefined || actual === null) return false;
  const accepted = Array.isArray(wanted) ? wanted : [wanted];
  const held = String(actual).toLowerCase();

  return accepted.some((candidate) => held === String(candidate).toLowerCase());
};

/** Every entry has to agree. An empty combination matches nothing, so a blank rule is inert. */
export const matchesCombination = (
  item: Record<string, unknown>,
  combination: Combination,
  lookIn: string[] = [],
): boolean => {
  const entries = Object.entries(combination ?? {});
  if (entries.length === 0) return false;

  return entries.every(([key, wanted]) => agrees(itemField(item, key, lookIn), wanted));
};

export const matchesAny = (
  item: Record<string, unknown>,
  combinations: Combination[] = [],
  lookIn: string[] = [],
): boolean => combinations.length > 0 && combinations.some((combination) => matchesCombination(item, combination, lookIn));

const ruleApplies = (document: Record<string, unknown>, rule: Reassignment): boolean => {
  if (!rule.when || !rule.when.field || (rule.when.equals ?? []).length === 0) return true;
  const actual = getValue(document, rule.when.field);

  return agrees(actual, rule.when.equals);
};

/** A copy deep enough that changing one leg never shows up in another. */
const copy = <T>(value: T): T => (value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T));

/** The unit a document says it belongs to, when the author named that field. */
const unitOf = (document: Record<string, unknown>, config: Reshape): string | undefined =>
  config.unitField ? String(getValue(document, config.unitField) ?? '') || undefined : undefined;

const withItems = (
  document: Record<string, unknown>,
  itemsPath: string,
  items: Record<string, unknown>[],
): Record<string, unknown> => {
  const next = copy(document);
  setValue(next, itemsPath, items);

  return next;
};

/**
 * Totals counted back up from the items that are actually on this leg.
 *
 * Only the totals an author listed are touched. A document carries fields nobody asked us to
 * recount, and quietly overwriting one would be worse than leaving it stale.
 */
export const recount = (
  document: Record<string, unknown>,
  config: Reshape,
  recalculate: Recalculate,
): Record<string, unknown> => {
  const items = asArray(getValue(document, config.itemsPath));
  const next = copy(document);

  for (const sum of recalculate.sums ?? []) {
    if (!sum.target) continue;
    let total = 0;
    for (const item of items) {
      for (const field of sum.from ?? []) {
        const held = itemField(item, field, config.lookIn ?? []);
        if (held !== undefined && held !== null && held !== '' && !Number.isNaN(Number(held))) {
          total += Number(held);
          break;
        }
      }
    }
    setValue(next, sum.target, round2(total));
  }

  const money = recalculate.money;
  if (money?.path) {
    const splits = asArray(getValue(next, money.path));
    if (splits.length > 0) {
      const held =
        money.heldMode && money.heldFrom
          ? round2(
              items.reduce((running, item) => {
                const value = itemField(item, money.heldFrom!, config.lookIn ?? []);

                return running + (Number(value) || 0);
              }, 0),
            )
          : 0;
      setValue(next, money.path, redistribute(splits, next, config, money, held));
    }
  }

  return next;
};

/**
 * Payment split across the new total.
 *
 * The methods that were used are kept as they are; only their sizes move, in the proportion they
 * were in before. Rounding leaves a penny somewhere, and it is put on the last scaled method
 * rather than dropped, so the parts always add back up to the whole.
 */
const redistribute = (
  splits: Record<string, unknown>[],
  document: Record<string, unknown>,
  config: Reshape,
  money: SplitMoney,
  heldFromItems: number,
): Record<string, unknown>[] => {
  const items = asArray(getValue(document, config.itemsPath));
  const isHeld = (split: Record<string, unknown>): boolean =>
    !!money.heldMode && String(split[money.modeField] ?? '').toLowerCase() === money.heldMode.toLowerCase();

  // what the whole document is now worth: the first sum an author named, else what was paid
  const target = (config.recalculate?.sums ?? [])[0]?.target;
  const whole = target !== undefined ? Number(getValue(document, target) ?? 0) : Number.NaN;
  const total = Number.isNaN(whole)
    ? splits.reduce((running, split) => running + (Number(split[money.valueField]) || 0), 0)
    : whole;
  if (items.length === 0 || total === 0) return splits;

  const heldBefore = round2(
    splits.filter(isHeld).reduce((running, split) => running + (Number(split[money.valueField]) || 0), 0),
  );
  const heldNow = heldFromItems > 0 ? round2(heldFromItems) : heldBefore;
  const anyHeld = splits.some(isHeld);
  const scaledTarget = round2(total - (anyHeld ? heldNow : 0));
  const scaledBefore = splits
    .filter((split) => !isHeld(split))
    .reduce((running, split) => running + (Number(split[money.valueField]) || 0), 0);

  const next = splits.map((split) => {
    if (isHeld(split)) return { ...split, [money.valueField]: heldNow };
    const was = Number(split[money.valueField]) || 0;

    return {
      ...split,
      [money.valueField]: scaledBefore === 0 ? 0 : round2((was / scaledBefore) * scaledTarget),
    };
  });

  const residual = round2(total - next.reduce((running, split) => running + Number(split[money.valueField] ?? 0), 0));
  if (residual !== 0) {
    for (let index = next.length - 1; index >= 0; index -= 1) {
      if (isHeld(next[index])) continue;
      next[index] = {
        ...next[index],
        [money.valueField]: round2(Number(next[index][money.valueField] ?? 0) + residual),
      };
      break;
    }
  }

  return next;
};

/**
 * Whether this document reverses a whole bill, and so needs the original looked up.
 *
 * Both conditions matter. The kind field says what it is; the absence of items says it is the
 * whole bill rather than part of one, because a partial return carries the lines that came back
 * and can be handled like any other document.
 */
export const reversesAWholeBill = (config: Reshape | undefined, input: unknown): string | undefined => {
  const reversal = config?.reversal;
  if (!reversal?.when?.field || !(reversal.when.equals ?? []).length || !reversal.originalField) return undefined;
  if (!input || typeof input !== 'object' || Array.isArray(input)) return undefined;

  const document = input as Record<string, unknown>;
  if (!agrees(getValue(document, reversal.when.field), reversal.when.equals)) return undefined;
  if (config?.itemsPath && asArray(getValue(document, config.itemsPath)).length > 0) return undefined;

  const original = getValue(document, reversal.originalField);
  const named = original === undefined || original === null ? '' : String(original);

  return named || undefined;
};

const negated = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const held = Number(value);

  return Number.isNaN(held) ? undefined : (held === 0 ? 0 : -held);
};

/**
 * One document per unit the original bill reached, each reversing that unit's own share.
 *
 * With no legs there is nothing to attribute the reversal to, so the document travels on as it
 * arrived rather than being guessed at or dropped: the unit it was reported to is the best
 * answer available, and a wrong answer here is a wrong ledger.
 */
export const reverseAcross = (config: Reshape, document: Record<string, unknown>, legs: OriginalLeg[]): Leg[] => {
  const reversal = config.reversal;
  if (!reversal || legs.length === 0) {
    return [{ document: copy(document), unitId: unitOf(document, config), why: ['the original was not found'] }];
  }

  return legs.map((leg) => {
    const made = copy(document);
    for (const [field, from] of Object.entries(reversal.set ?? {})) {
      const held = leg[from];
      if (held !== undefined && held !== null) setValue(made, field, held);
    }
    for (const [field, from] of Object.entries(reversal.negate ?? {})) {
      const turned = negated(leg[from]);
      if (turned !== undefined) setValue(made, field, turned);
    }

    return {
      document: made,
      unitId: unitOf(made, config),
      why: [`reversing what the original left at ${unitOf(made, config) ?? 'this unit'}`],
    };
  });
};

/**
 * The whole reshaping, in the order the rule server does it: take items out, move items across,
 * then count what is left.
 *
 * A document that had items and lost all of them is dropped rather than sent on empty — an empty
 * document is not a smaller sale, it is a sale that did not happen here. A document that never
 * had items is left alone, because that is how a cancellation arrives.
 */
const skips = (config: Reshape, document: Record<string, unknown>): boolean =>
  (config.skip ?? []).some((rule) => {
    const held = getValue(document, rule.field);
    return held !== undefined && held !== null && rule.equals.some((value) => String(held).toLowerCase() === value.toLowerCase());
  });

export const applyReshape = (
  config: Reshape | undefined,
  input: unknown,
  fields?: FieldMap,
  /** The original bill's legs, when this document reverses a whole bill and they were looked up. */
  original?: OriginalLeg[],
): Leg[] => {
  const document = input && typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>) : undefined;
  if (!document) return [];
  if (!config?.itemsPath) return [{ document: document, why: ['nothing is configured on this node'] }];
  config = translateReshape(config, fields);
  if (skips(config, document)) return [];

  /*
   * A document reversing a whole bill is answered from the original, not from its own items —
   * it has none. Everything below works by moving and counting items, and would leave nothing.
   */
  if (reversesAWholeBill(config, document)) return reverseAcross(config, document, original ?? []);

  const lookIn = config.lookIn ?? [];
  const before = asArray(getValue(document, config.itemsPath));
  const hadItems = before.length > 0;

  const excluded = config.exclude?.length
    ? before.filter((item) => !config.exclude!.some((rule) => matchesAny(item, rule.match ?? [], lookIn)))
    : before;

  const why: string[] = [];
  if (excluded.length !== before.length) why.push(`${before.length - excluded.length} item(s) excluded`);
  if (hadItems && excluded.length === 0) return [];

  /*
   * A document that arrived with no items has nothing to move. Running the reassignment over it
   * would find nothing to take and nothing to leave, and it would vanish — which is how a
   * cancellation stops reaching the unit it is cancelling at.
   */
  if (!hadItems) return [{ document: copy(document), unitId: unitOf(document, config), why: [...why] }];

  const legs: Leg[] = [];
  const applicable = (config.reassign ?? []).filter((rule) => ruleApplies(document, rule));

  if (applicable.length === 0) {
    legs.push({ document: withItems(document, config.itemsPath, excluded), why: [...why] });
  } else {
    /*
     * An item can only move once. Rules are tried in the order they were written, and the first
     * one that wants an item takes it — otherwise two rules could each claim the same line and
     * the same money would be counted twice.
     */
    const claimed = new Set<number>();
    for (const rule of applicable) {
      const taken: Record<string, unknown>[] = [];
      excluded.forEach((item, index) => {
        if (claimed.has(index)) return;
        const hit = matchesAny(item, rule.match ?? [], lookIn);
        if (rule.moveMatching ? hit : !hit) {
          taken.push(item);
          claimed.add(index);
        }
      });
      if (taken.length === 0) continue;

      const moved = withItems(document, config.itemsPath, taken);
      for (const [field, value] of Object.entries(rule.set ?? {})) setValue(moved, field, value);
      legs.push({
        document: moved,
        unitId: unitOf(moved, config),
        why: [...why, `${taken.length} item(s) moved by "${rule.label ?? rule.id}"`],
      });
    }

    const left = excluded.filter((_item, index) => !claimed.has(index));
    if (left.length > 0) {
      legs.unshift({
        document: withItems(document, config.itemsPath, left),
        unitId: unitOf(document, config),
        why: [...why, `${left.length} item(s) stayed`],
      });
    }
  }

  if (!config.recalculate?.sums?.length) return legs;

  /*
   * Only a leg that has items is counted again. A document that arrived without any carries
   * totals of its own — that is how a cancellation says what it is reversing — and writing
   * zeroes over them would turn a reversal into nothing at all.
   */
  return legs.map((leg) =>
    asArray(getValue(leg.document, config.itemsPath)).length === 0
      ? leg
      : {
          ...leg,
          document: recount(leg.document, config, config.recalculate!),
          why: [...leg.why, 'totals counted again'],
        },
  );
};

/** Why this configuration cannot be saved, in the author's words, or nothing when it is fine. */
export const reshapeFault = (config: Reshape): string | undefined => {
  if (!config.itemsPath?.trim()) return 'Say which field holds the items.';

  for (const rule of config.exclude ?? []) {
    if (!(rule.match ?? []).length) return `"${rule.label ?? rule.id}" leaves out nothing. Give it something to match.`;
    if (rule.match.some((combination) => Object.keys(combination ?? {}).length === 0)) {
      return `"${rule.label ?? rule.id}" has an empty match, which would never fire.`;
    }
  }

  for (const rule of config.reassign ?? []) {
    if (Object.keys(rule.set ?? {}).length === 0) {
      return `"${rule.label ?? rule.id}" moves items without saying where. Set at least one field.`;
    }
    if (rule.moveMatching && !(rule.match ?? []).length) {
      return `"${rule.label ?? rule.id}" moves what matches, but nothing is matched yet.`;
    }
  }

  for (const sum of config.recalculate?.sums ?? []) {
    if (!sum.target?.trim()) return 'Every total needs a field to write into.';
    if (!(sum.from ?? []).filter(Boolean).length) return `"${sum.target}" has nothing to add up.`;
  }

  for (const rule of config.skip ?? []) {
    if (!rule.field?.trim() || !(rule.equals ?? []).filter(Boolean).length) {
      return 'A skip rule needs a field and at least one value.';
    }
  }

  const reversal = config.reversal;
  if (reversal) {
    if (!reversal.when?.field?.trim() || !(reversal.when.equals ?? []).filter(Boolean).length) {
      return 'Say which field marks a reversal, and which values mean it.';
    }
    if (!reversal.originalField?.trim()) return 'Say which field names the bill being reversed.';
    if (Object.keys(reversal.negate ?? {}).length === 0 && Object.keys(reversal.set ?? {}).length === 0) {
      return 'A reversal that copies nothing from the original would be the same document twice.';
    }
    for (const [field, from] of Object.entries({ ...(reversal.set ?? {}), ...(reversal.negate ?? {}) })) {
      if (!field.trim() || !from.trim()) return 'Every line of a reversal needs a field on both sides.';
    }
  }

  const money = config.recalculate?.money;
  if (money?.path) {
    if (!money.valueField?.trim() || !money.modeField?.trim()) {
      return 'Payment splits need the field holding the amount and the field holding the method.';
    }
    if (money.heldMode && !money.heldFrom?.trim()) {
      return `Say which item field says how much "${money.heldMode}" was used.`;
    }
  }

  return undefined;
};
